import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import Backdrop from "@mui/material/Backdrop";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import LogoutIcon from "@mui/icons-material/Logout";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

// Services and Context
import AuthService from "services/auth-service";
import { AuthContext } from "context";
import UserService from "services/user-service";

// Components
import Plan from "layouts/profile/components/Subscription/plan";

function ExpiredSubscription() {
    const [view, setView] = useState("message"); // 'message' | 'plan'
    const authContext = useContext(AuthContext);
    const navigate = useNavigate();

    const handleLogOut = async () => {
        try {
            await AuthService.logout();
            authContext.logout();
            navigate("/auth/login");
        } catch (error) {
            console.error("Logout failed", error);
        }
    };

    // Kebab menu state
    const [kebabAnchor, setKebabAnchor] = useState(null);
    const handleKebabOpen = (e) => setKebabAnchor(e.currentTarget);
    const handleKebabClose = () => setKebabAnchor(null);

    // Workspace switcher state
    const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState(null);
    const [workspaces, setWorkspaces] = useState([]);
    const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);

    const handleWorkspaceMenuOpen = async (e) => {
        setWorkspaceMenuAnchor(e.currentTarget);
        setLoadingWorkspaces(true);
        try {
            const response = await UserService.fetchUserWorkspaces();
            const list = Array.isArray(response) ? response : (response.data || []);
            setWorkspaces(list);
        } catch (err) {
            console.error("Failed to fetch workspaces", err);
        } finally {
            setLoadingWorkspaces(false);
        }
    };
    const handleWorkspaceMenuClose = () => setWorkspaceMenuAnchor(null);
    const handleSwitchWorkspace = async (workspaceId) => {
        try {
            await UserService.switchActiveWorkspace(workspaceId);
            localStorage.setItem("activeWorkspaceId", workspaceId);
            window.dispatchEvent(new CustomEvent("profile-updated"));
            handleWorkspaceMenuClose();
            handleKebabClose();
        } catch (err) {
            console.error("Failed to switch workspace", err);
        }
    };

    return (
        <Backdrop
            sx={{
                color: "#fff",
                zIndex: (theme) => theme.zIndex.drawer + 30,
                backdropFilter: "blur(10px)",
                backgroundColor: "rgba(0, 0, 0, 0.8)"
            }}
            open={true}
        >
            {view === "message" && (
                <Card sx={{ maxWidth: 500, width: "90%", p: 4, textAlign: "center", position: "relative" }}>

                    {/* 3-dot kebab menu — top right */}
                    <IconButton
                        size="small"
                        onClick={handleKebabOpen}
                        sx={{ position: "absolute", top: 8, right: 8, color: "text.secondary" }}
                    >
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        anchorEl={kebabAnchor}
                        open={Boolean(kebabAnchor)}
                        onClose={handleKebabClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        sx={{ mt: 0.5, zIndex: 9999 }}
                    >
                        <MenuItem onClick={handleWorkspaceMenuOpen}>
                            <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
                            Switch Workspace
                        </MenuItem>
                        <Menu
                            anchorEl={workspaceMenuAnchor}
                            open={Boolean(workspaceMenuAnchor)}
                            onClose={handleWorkspaceMenuClose}
                            anchorOrigin={{ vertical: "top", horizontal: "left" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                            sx={{ zIndex: 10000 }}
                        >
                            {loadingWorkspaces ? (
                                <MenuItem disabled><CircularProgress size={16} sx={{ mr: 1 }} /> Loading...</MenuItem>
                            ) : workspaces.length > 0 ? (
                                workspaces.map((ws) => (
                                    <MenuItem
                                        key={ws.workspace_id?._id || ws._id}
                                        onClick={() => handleSwitchWorkspace(ws.workspace_id?._id || ws._id)}
                                        selected={ws.is_active}
                                    >
                                        {ws.workspace_id?.name || "Unknown"} {ws.is_active && "(Active)"}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem disabled>No other workspaces</MenuItem>
                            )}
                        </Menu>
                        <MenuItem onClick={() => { handleKebabClose(); handleLogOut(); }}>
                            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                            Log Out
                        </MenuItem>
                    </Menu>

                    <MDBox
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="60px"
                        height="60px"
                        borderRadius="50%"
                        bgcolor="#FFEBEE" // Light red
                        mx="auto"
                        mb={2}
                    >
                        <Icon fontSize="large" color="error">error_outline</Icon>
                    </MDBox>

                    <MDTypography variant="h4" fontWeight="bold" gutterBottom>
                        Subscription Expired
                    </MDTypography>

                    <MDTypography variant="body2" color="text" mb={3}>
                        Your subscription plan has expired or the renewal payment failed.
                        To continue accessing your dashboard and data, please renew your subscription.
                    </MDTypography>

                    <MDBox display="flex" justifyContent="center" gap={2}>
                        <MDButton
                            variant="contained"
                            color="accent"
                            onClick={() => setView("plan")}
                        >
                            Renew Subscription
                        </MDButton>
                    </MDBox>
                </Card>
            )}

            {/* Plan View (No Dialog) */}
            {view === "plan" && (
                <MDBox sx={{ width: "95%", maxWidth: "1600px", maxHeight: "90vh", overflowY: "auto" }}>
                    <Plan onClose={() => setView("message")} />
                </MDBox>
            )}
        </Backdrop>
    );
}

export default ExpiredSubscription;
